import { EditedRecord } from '@modules/feedback/store/types';
import { getReasonPredictions } from '@modules/feedback/utils';
import {
  LabelType,
  ManipulationType,
  SuggestedReason,
} from 'generated/graphql';
import { FeedbackLabelType } from 'v2/core/entities/feedback';
import { ReasonLabel } from 'v2/core/entities/feedback/labels';
import { mapLabelData } from 'v2/core/repository/edge-server/labels/mappers';

export interface ReasonSuggestions {
  label: ReasonLabel;
  relatedRCs: string[];
}

export const getReasonSuggestionsFromSuggestedReasons = (
  suggestedReasons: SuggestedReason[],
  editedRecords: EditedRecord[] = [],
): ReasonSuggestions[] => {
  const alreadyAddedReasonSuggestions = editedRecords.flatMap((record) => {
    const reasonPrediction = getReasonPredictions(record);
    if (!reasonPrediction) {
      return [];
    }

    return reasonPrediction
      .filter(
        (prediction) => prediction.manipulationType === ManipulationType.Add,
      )
      .flatMap(({ prediction, relatedRCs }) => {
        if (prediction.category) {
          return [prediction.id];
        }
        return relatedRCs ?? [];
      });
  });

  const result: ReasonSuggestions[] = [];

  const reasonCandidates = new Set<string>();

  for (const suggestedReason of suggestedReasons) {
    const label = suggestedReason.label;
    if (label) {
      const reasonOrEntityLabel = mapLabelData(label);
      const mappedLabel: ReasonLabel = {
        id: label.id,
        displayName: label.displayName,
        type: FeedbackLabelType.Reason,
        isVisible: label.isVisible,
        category:
          reasonOrEntityLabel.type === LabelType.Reason
            ? reasonOrEntityLabel.metadata.category
            : undefined,
      };
      const relatedRCs = suggestedReason.relatedRCs ?? [];
      relatedRCs.forEach((reasonCandidate) =>
        reasonCandidates.add(reasonCandidate),
      );
      result.push({ label: mappedLabel, relatedRCs });
    }
  }

  const reasonSuggestionsFromReasonCandidates: ReasonSuggestions[] = Array.from(
    reasonCandidates,
  ).map((reasonCandidate) => ({
    label: {
      id: reasonCandidate,
      displayName: reasonCandidate,
      type: FeedbackLabelType.Reason,
      isVisible: true,
    },
    relatedRCs: [],
  }));

  return [...result, ...reasonSuggestionsFromReasonCandidates].filter(
    ({ label }) => !alreadyAddedReasonSuggestions.includes(label.id),
  );
};
